import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Users,
  Search,
  RefreshCcw,
  ChevronDown,
  ChevronUp,
  Filter,
  FileDown,
  FolderPlus,
  MessageSquareText,
  TrendingUp,
  FileText,
  Image as ImageIcon,
  PlusCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const aspxSelect = (v) => {
  const s = (v ?? "").toString().trim();
  return s === "" ? "Select" : s;
};

const EmployeeManagementList = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = Number(localStorage.getItem("finYearId") || 0);
  const userId = Number(localStorage.getItem("userId") || 1);
  const branchType = (localStorage.getItem("branchType") || "HO").toString().trim();

  const [showFilters, setShowFilters] = useState(true);
  const screenBadge = useMemo(() => "INFRA SCREEN : EMPLOYEE MANAGEMENT", []);

  // dropdowns
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [grades, setGrades] = useState([]);

  // filters (ASP)
  const [branchId, setBranchId] = useState(() => {
    const saved = (localStorage.getItem("branchId") || "").toString().trim();
    return saved && saved !== "Select" ? saved : "";
  });
  const [deptId, setDeptId] = useState("");
  const [desigId, setDesigId] = useState("");
  const [gradeId, setGradeId] = useState("");
  const [pf, setPf] = useState("");
  const [paymode, setPaymode] = useState("");
  const [empName, setEmpName] = useState("");
  const [empCode, setEmpCode] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [didInitialSearch, setDidInitialSearch] = useState(false);

  // delete selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // resign selection (ASP has it in grid)
  const [resignSelectedIds, setResignSelectedIds] = useState([]);
  const [resignSelectAll, setResignSelectAll] = useState(false);
  const [resignDates, setResignDates] = useState({}); // { [employeeId]: "yyyy-mm-dd" }

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const ensureFinYear = () => {
    if (!finYearId) {
      toast.warn("Please select Financial Year.");
      return false;
    }
    return true;
  };

  // styling
  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const dangerBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (selectedIds.length === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700");

  // robust row getters (works for different SP column names)
  const getRowEmployeeId = (r) =>
    Number(r?.fld_employeeid || r?.Employeeid || r?.employeeId || r?.fld_Employeeid || 0);

  const getRowEmpCode = (r) =>
    String(r?.fld_employeecode || r?.Employeecode || r?.empCode || r?.fld_Employeecode || "").trim();

  const getRowEmpName = (r) =>
    String(r?.fld_Employee || r?.Employee || r?.employee || r?.EmployeeName || "").trim();

  const getRowDesig = (r) =>
    String(r?.fld_designationname || r?.desigName || r?.DesignationName || r?.designation || "-").trim();

  const getRowBranchName = (r) =>
    String(r?.fld_branchname || r?.branchname || r?.BranchName || "-").trim();

  const getRowPhoto = (r) =>
    String(r?.fld_photo || r?.Photo || r?.fld_empphoto || r?.fld_Photo || "").trim();

  // navigation actions (same feel as ASP links)
  const onClickEmpCode = (r) => {
    const employeeId = getRowEmployeeId(r);
    if (!employeeId) return toast.warn("employeeId not found");
    navigate(`/PayRoll/EmployeeManagement/${employeeId}`); // edit page
  };

  const onClickAdd = () => navigate(`/PayRoll/EmployeeManagement/new`);

  const onClickPFForms = (r) => {
    const employeeId = getRowEmployeeId(r);
    if (!employeeId) return toast.warn("employeeId not found");
    navigate(`/PayRoll/EmployeePFForms/${employeeId}`);
  };

  const onClickRemarks = (r) => {
    const employeeId = getRowEmployeeId(r);
    if (!employeeId) return toast.warn("employeeId not found");
    navigate(`/PayRoll/EmployeeRemarks/${employeeId}`);
  };

  const onClickPerformance = (r) => {
    const employeeId = getRowEmployeeId(r);
    if (!employeeId) return toast.warn("employeeId not found");
    navigate(`/PayRoll/EmployeePerformance/${employeeId}`);
  };

  const onClickFileManager = (r) => {
    const employeeId = getRowEmployeeId(r);
    if (!employeeId) return toast.warn("employeeId not found");
    navigate(`/PayRoll/EmployeeFileManager/${employeeId}`);
  };

  const PhotoCell = ({ row }) => {
    const filename = getRowPhoto(row);
    const employeeId = getRowEmployeeId(row);

    if (!filename) {
      return (
        <div className="flex items-center justify-center gap-2 opacity-70">
          <ImageIcon className="w-4 h-4" />
          <span className="text-xs">No Photo</span>
        </div>
      );
    }

    return (
      <button
        type="button"
        onClick={() => navigate(`/PayRoll/EmployeeManagement/${employeeId}`)}
        title="Open details"
        className="inline-flex items-center justify-center"
      >
        <img
          src={`${BASE_URL}/employee_photo/${filename}`}
          alt="Emp"
          className="w-12 h-12 object-cover rounded-xl border"
        />
      </button>
    );
  };

  // data load
  const fetchListWithBranchOverride = async (branchOverride) => {
    if (!ensureFinYear()) return;

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/employee-management/list`, {
        params: {
          finYearId,
          branchType,
          branchId: aspxSelect(branchOverride ?? branchId),
          desigId: aspxSelect(desigId),
          deptId: aspxSelect(deptId),
          gradeId: aspxSelect(gradeId),
          pf: aspxSelect(pf),
          paymode: aspxSelect(paymode),
          empName: empName.trim(),
          empCode: empCode.trim(),
          fromDate: fromDate || "",
          toDate: toDate || "",
        },
      });

      if (res?.data?.status) {
        const list = res.data.data || [];
        setRows(list);

        // reset grid state like ASP on new bind
        setSelectedIds([]);
        setSelectAll(false);

        setResignSelectedIds([]);
        setResignSelectAll(false);
        setResignDates({});

        // reset pagination
        setPage(1);

        toast.success(`Loaded ${list.length} record(s)`);
      } else {
        toast.error(res?.data?.message || "Failed to load list");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "List load failed");
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async () => fetchListWithBranchOverride(branchId);

  const loadDropdowns = async () => {
    if (!ensureFinYear()) return;
    try {
      setLoading(true);
      const [b, d, de, g] = await Promise.all([
        axios.get(`${BASE_URL}/api/employee-management/branches`, { params: { finYearId } }),
        axios.get(`${BASE_URL}/api/employee-management/departments`, { params: { finYearId } }),
        axios.get(`${BASE_URL}/api/employee-management/designations`, { params: { finYearId } }),
        axios.get(`${BASE_URL}/api/employee-management/grades`, { params: { finYearId } }),
      ]);

      setBranches(b?.data?.data || []);
      setDepartments(d?.data?.data || []);
      setDesignations(de?.data?.data || []);
      setGrades(g?.data?.data || []);

      if (!didInitialSearch) {
        const loginBranch = (localStorage.getItem("branchId") || "").toString().trim();
        const effectiveBranch = loginBranch && loginBranch !== "Select" ? loginBranch : branchId;

        if (effectiveBranch && effectiveBranch !== branchId) setBranchId(effectiveBranch);
        setDidInitialSearch(true);

        await fetchListWithBranchOverride(effectiveBranch);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Dropdown load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Clear filters back to empty and reload with current branch context
  const handleClearFilters = () => {
    setDeptId("");
    setDesigId("");
    setGradeId("");
    setPf("");
    setPaymode("");
    setEmpName("");
    setEmpCode("");
    setFromDate("");
    setToDate("");
    fetchListWithBranchOverride(branchId);
  };

  // delete selection
  const toggleSelect = (employeeId) => {
    setSelectedIds((prev) =>
      prev.includes(employeeId) ? prev.filter((x) => x !== employeeId) : [...prev, employeeId]
    );
  };

  const handleSelectAll = () => {
    const idsOnPage = pagedRows.map((r) => getRowEmployeeId(r)).filter(Boolean);
    if (selectAll) {
      setSelectedIds((prev) => prev.filter((id) => !idsOnPage.includes(id)));
      setSelectAll(false);
      return;
    }
    setSelectedIds((prev) => Array.from(new Set([...prev, ...idsOnPage])));
    setSelectAll(true);
  };

  // resign selection (ASP-like)
  const toggleResignSelect = (employeeId) => {
    setResignSelectedIds((prev) =>
      prev.includes(employeeId) ? prev.filter((x) => x !== employeeId) : [...prev, employeeId]
    );
  };

  const handleResignSelectAll = () => {
    const idsOnPage = pagedRows.map((r) => getRowEmployeeId(r)).filter(Boolean);
    if (resignSelectAll) {
      setResignSelectedIds((prev) => prev.filter((id) => !idsOnPage.includes(id)));
      setResignSelectAll(false);
      return;
    }
    setResignSelectedIds((prev) => Array.from(new Set([...prev, ...idsOnPage])));
    setResignSelectAll(true);
  };

  const bulkDelete = async () => {
    if (!ensureFinYear()) return;
    if (selectedIds.length === 0) return toast.warn("Select at least one employee");

    if (!window.confirm(`Delete ${selectedIds.length} employee(s)? This cannot be undone.`)) return;

    try {
      setLoading(true);

      const selectedRows = rows.filter((r) => selectedIds.includes(getRowEmployeeId(r)));
      const items = selectedRows.map((r) => ({
        employeeId: getRowEmployeeId(r),
        empCode: getRowEmpCode(r),
      }));

      const res = await axios.post(`${BASE_URL}/api/employee-management/bulk/delete`, {
        finYearId,
        userId,
        items,
      });

      if (res?.data?.status) {
        toast.success(res?.data?.message || "Deleted");
        await fetchList();
      } else {
        toast.error(res?.data?.message || "Bulk delete failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Bulk delete failed");
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = () => {
    if (!ensureFinYear()) return;

    const params = new URLSearchParams({
      finYearId: String(finYearId),
      branchId: aspxSelect(branchId),
      desigId: aspxSelect(desigId),
      deptId: aspxSelect(deptId),
      gradeId: aspxSelect(gradeId),
      pf: aspxSelect(pf),
      empName: empName.trim(),
      empCode: empCode.trim(),
    });

    window.open(`${BASE_URL}/api/employee-management/export?${params.toString()}`, "_blank");
  };

  // ---------------- Pagination ----------------
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const clampedPage = Math.min(Math.max(1, page), totalPages);

  useEffect(() => {
    if (page !== clampedPage) setPage(clampedPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages]);

  const startIdx = (clampedPage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const pagedRows = rows.slice(startIdx, endIdx);

  // keep header selectAll in sync for current page
  useEffect(() => {
    const idsOnPage = pagedRows.map((r) => getRowEmployeeId(r)).filter(Boolean);
    const allSelectedOnPage = idsOnPage.length > 0 && idsOnPage.every((id) => selectedIds.includes(id));
    setSelectAll(allSelectedOnPage);

    const allResignSelectedOnPage = idsOnPage.length > 0 && idsOnPage.every((id) => resignSelectedIds.includes(id));
    setResignSelectAll(allResignSelectedOnPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagedRows, selectedIds, resignSelectedIds]);

  const pageNumbers = useMemo(() => {
    // show up to 7 numbers like: 1 ... 4 5 6 ... 20
    const max = 7;
    if (totalPages <= max) return Array.from({ length: totalPages }, (_, i) => i + 1);

    const nums = [];
    const left = Math.max(1, clampedPage - 2);
    const right = Math.min(totalPages, clampedPage + 2);

    nums.push(1);
    if (left > 2) nums.push("...");
    for (let p = left; p <= right; p++) {
      if (p !== 1 && p !== totalPages) nums.push(p);
    }
    if (right < totalPages - 1) nums.push("...");
    nums.push(totalPages);

    return nums;
  }, [clampedPage, totalPages]);

  // ------------------------------------------------

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2600}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE MANAGEMENT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Users className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Employee Management
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  ASP columns + paging
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {rows.length} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={onClickAdd} className={primaryBtn} disabled={loading}>
                <PlusCircle className="w-4 h-4" />
                Add New
              </button>

              <button onClick={downloadExcel} className={ghostBtn} disabled={loading}>
                <FileDown className="w-4 h-4" />
                Excel
              </button>

              <button onClick={loadDropdowns} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Reload Masters
              </button>

              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={cardBase + " p-6 mb-6"}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <Filter className={isDark ? "text-gray-200" : "text-gray-700"} />
                  <div className="text-lg font-extrabold">Search Criteria</div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button onClick={handleClearFilters} className={ghostBtn} disabled={loading}>
                    Clear
                  </button>

                  <button onClick={fetchList} className={primaryBtn} disabled={loading}>
                    <Search className="w-4 h-4" />
                    Search
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className={labelBase}>Employee Name</label>
                  <input
                    className={inputBase}
                    value={empName}
                    onChange={(e) => setEmpName(e.target.value)}
                    placeholder="Employee Name"
                  />
                </div>

                <div>
                  <label className={labelBase}>Employee Code</label>
                  <input
                    className={inputBase}
                    value={empCode}
                    onChange={(e) => setEmpCode(e.target.value)}
                    placeholder="Employee Code"
                  />
                </div>

                <div>
                  <label className={labelBase}>Branch</label>
                  <select
                    className={inputBase}
                    value={branchId}
                    onChange={(e) => {
                      const v = e.target.value;
                      setBranchId(v);
                      localStorage.setItem("branchId", String(v || ""));
                    }}
                  >
                    <option value="">Select</option>
                    {branches.map((b, idx) => (
                      <option key={idx} value={b?.fld_Branchid ?? b?.branchId ?? b?.BranchId ?? ""}>
                        {b?.fld_BranchName ?? b?.branchName ?? b?.BranchName ?? "Branch"}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Department</label>
                  <select className={inputBase} value={deptId} onChange={(e) => setDeptId(e.target.value)}>
                    <option value="">Select</option>
                    {departments.map((d) => (
                      <option key={d.deptId} value={d.deptId}>
                        {d.deptName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Grade</label>
                  <select className={inputBase} value={gradeId} onChange={(e) => setGradeId(e.target.value)}>
                    <option value="">Select</option>
                    {grades.map((g) => (
                      <option key={g.gradeId} value={g.gradeId}>
                        {g.gradeName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Designation</label>
                  <select className={inputBase} value={desigId} onChange={(e) => setDesigId(e.target.value)}>
                    <option value="">Select</option>
                    {designations.map((d) => (
                      <option key={d.desigId} value={d.desigId}>
                        {d.desigName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>PF/Non PF</label>
                  <select className={inputBase} value={pf} onChange={(e) => setPf(e.target.value)}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Honorarium">Honorarium</option>
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Payment Mode</label>
                  <select className={inputBase} value={paymode} onChange={(e) => setPaymode(e.target.value)}>
                    <option value="">Select</option>
                    <option value="Cash">Cash</option>
                    <option value="Bank">Bank</option>
                  </select>
                </div>

                <div>
                  <label className={labelBase}>From Date</label>
                  <input type="date" className={inputBase} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </div>

                <div>
                  <label className={labelBase}>To Date</label>
                  <input type="date" className={inputBase} value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* ==================== BULK ACTIONS + PAGE SIZE ==================== */}
          <div className={cardBase + " p-6 mb-6"}>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                <div
                  className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                    isDark ? "bg-white/10 text-white" : "bg-gray-100 text-gray-800"
                  }`}
                >
                  Selected: {selectedIds.length}
                </div>

                <button onClick={bulkDelete} className={dangerBtn} disabled={loading || selectedIds.length === 0}>
                  🗑 Bulk Delete
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-3 justify-end">
                <div className={`text-sm font-bold ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  Page Size
                </div>
                <select
                  className={`${inputBase} !py-2 !px-3 !rounded-xl !w-[110px]`}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>

                <div className={`text-sm opacity-70 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                  Showing {total === 0 ? 0 : startIdx + 1} - {Math.min(endIdx, total)} of {total}
                </div>
              </div>
            </div>
          </div>

          {/* ==================== RESULTS TABLE ==================== */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="min-w-[1400px] w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-center font-bold">Name</th>
                    <th className="px-4 py-4 text-center font-bold">Designation</th>
                    <th className="px-4 py-4 text-center font-bold">BranchName</th>

                    <th className="px-4 py-4 text-center font-bold">Photo</th>
                    <th className="px-4 py-4 text-center font-bold">PF Forms</th>
                    <th className="px-4 py-4 text-center font-bold">Remarks</th>
                    <th className="px-4 py-4 text-center font-bold">Performance</th>
                    <th className="px-4 py-4 text-center font-bold">File Manager</th>

                    <th className="px-4 py-4 text-center font-bold">
                      <div className="flex items-center justify-center gap-2">
                        Delete
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </div>
                    </th>

                    <th className="px-4 py-4 text-center font-bold">Resign Date</th>
                    <th className="px-4 py-4 text-center font-bold">
                      <div className="flex items-center justify-center gap-2">
                        Resign
                        <input
                          type="checkbox"
                          checked={resignSelectAll}
                          onChange={handleResignSelectAll}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: 8 }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 13 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : pagedRows.length === 0 ? (
                    <tr>
                      <td colSpan={13} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    pagedRows.map((r, idx) => {
                      const employeeId = getRowEmployeeId(r);
                      const code = getRowEmpCode(r);
                      const name = getRowEmpName(r) || "-";
                      const desig = getRowDesig(r);
                      const branchName = getRowBranchName(r);

                      const globalIndex = startIdx + idx + 1;

                      return (
                        <tr
                          key={employeeId || idx}
                          className={`${
                            idx % 2 === 0
                              ? isDark ? "bg-gray-800" : "bg-white"
                              : isDark ? "bg-gray-750" : "bg-gray-50"
                          } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {globalIndex}
                          </td>

                          <td className="px-4 py-4 text-sm text-center">
                            <button
                              onClick={() => onClickEmpCode(r)}
                              className={`inline-flex items-center gap-1 font-bold underline underline-offset-2 ${
                                isDark ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-800"
                              }`}
                              title="Open Employee"
                            >
                            {code || "OPEN"}
                            </button>
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {name}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {desig}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {branchName}
                          </td>

                          {/* Photo */}
                          <td className="px-4 py-4 text-center">
                            <PhotoCell row={r} />
                          </td>

                          {/* PF Forms */}
                          <td className="px-4 py-4 text-center">
                            <button
                              onClick={() => onClickPFForms(r)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-2xl text-sm font-bold ${
                                isDark ? "bg-white/10 text-white hover:bg-white/20" : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                              }`}
                            >
                              <FileText className="w-4 h-4" />
                              Add PF
                            </button>
                          </td>

                          {/* Remarks */}
                          <td className="px-4 py-4 text-center">
                            <button
                              onClick={() => onClickRemarks(r)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-2xl text-sm font-bold ${
                                isDark ? "bg-white/10 text-white hover:bg-white/20" : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                              }`}
                            >
                              <MessageSquareText className="w-4 h-4" />
                              Remarks
                            </button>
                          </td>

                          {/* Performance */}
                          <td className="px-4 py-4 text-center">
                            <button
                              onClick={() => onClickPerformance(r)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-2xl text-sm font-bold ${
                                isDark ? "bg-white/10 text-white hover:bg-white/20" : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                              }`}
                            >
                              <TrendingUp className="w-4 h-4" />
                              Performance
                            </button>
                          </td>

                          {/* File Manager */}
                          <td className="px-4 py-4 text-center">
                            <button
                              onClick={() => onClickFileManager(r)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-2xl text-sm font-bold ${
                                isDark ? "bg-white/10 text-white hover:bg-white/20" : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                              }`}
                            >
                              <FolderPlus className="w-4 h-4" />
                              Add
                            </button>
                          </td>

                          {/* Delete */}
                          <td className="px-4 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(employeeId)}
                              onChange={() => toggleSelect(employeeId)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>

                          {/* Resign Date */}
                          <td className="px-4 py-4 text-center">
                            <input
                              type="date"
                              className={`px-3 py-2 rounded-xl border text-sm ${
                                isDark ? "bg-gray-800/40 border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"
                              }`}
                              value={resignDates[employeeId] || ""}
                              onChange={(e) =>
                                setResignDates((prev) => ({
                                  ...prev,
                                  [employeeId]: e.target.value,
                                }))
                              }
                            />
                          </td>

                          {/* Resign */}
                          <td className="px-4 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={resignSelectedIds.includes(employeeId)}
                              onChange={() => toggleResignSelect(employeeId)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* ==================== PAGINATION FOOTER ==================== */}
            <div className={`p-4 border-t ${isDark ? "border-white/10" : "border-gray-200"} flex flex-col md:flex-row gap-3 items-center justify-between`}>
              <div className={`text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                Page {clampedPage} of {totalPages}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  className={ghostBtn + " !px-3 !py-2 !rounded-xl"}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={clampedPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Prev
                </button>

                {pageNumbers.map((p, idx) =>
                  p === "..." ? (
                    <span key={`dots-${idx}`} className={`px-3 py-2 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      ...
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`px-4 py-2 rounded-xl font-bold border transition ${
                        p === clampedPage
                          ? `${primaryBtnClass} text-white border-transparent`
                          : isDark
                            ? "border-white/10 text-white hover:bg-white/10"
                            : "border-gray-200 text-gray-800 hover:bg-gray-50"
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}

                <button
                  className={ghostBtn + " !px-3 !py-2 !rounded-xl"}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={clampedPage === totalPages}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          <div className="mt-4 text-sm opacity-70">
            Tip: The table has <b>all ASP columns</b> and supports <b>paging</b>.
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeManagementList;